import { useEffect, useMemo, useState } from 'react';
import {
  estimateFiveHundredTricks,
  fhAllContracts,
  fhBidValue,
  fhPartnerOf,
  fhTeamOf,
  sortHand,
  type FHContract,
  type FHSuit,
  type FiveHundredLegal,
  type FiveHundredView,
} from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣', N: 'NT' };
const BID_SUITS: FHSuit[] = ['S', 'C', 'D', 'H', 'N'];
const isRed = (s: string) => s === 'H' || s === 'D';
const contractLabel = (c: FHContract) => `${c.tricks}${SUIT_SYMBOL[c.suit]}`;

export function FiveHundredTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as FiveHundredView;
  const legal = useGame((s) => s.legal) as FiveHundredLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const [toDiscard, setToDiscard] = useState<string[]>([]);
  const [jokerPending, setJokerPending] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? fhTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const iMustDiscard = !!myTurn && legal?.kind === 'discard';
  const bidding = view.phase === 'BIDDING';
  const playing = view.phase === 'PLAYING' || view.phase === 'TRICK_END';
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const contract = view.highBid?.contract ?? null;
  const declarerTeam = view.declarer !== null ? fhTeamOf(view.declarer) : null;
  const teamTricks = (team: number) => (view.tricksWon[team] ?? 0) + (view.tricksWon[team + 2] ?? 0);

  // A new deal, phase or trick always starts with nothing selected.
  useEffect(() => {
    setToDiscard([]);
    setJokerPending(null);
    setSent(false);
  }, [view.round, view.phase]);

  const bidTag = (s: number) => {
    if (!bidding) return null;
    if (view.passed[s]) return t('ta.pass');
    return view.bids[s] ? contractLabel(view.bids[s]!) : null;
  };

  const play = (card: string, suit?: 'S' | 'H' | 'D' | 'C') => {
    setSelected(card);
    void act(suit ? { type: 'play', card, suit } : { type: 'play', card }).finally(() => setSelected(null));
  };

  const onCard = (c: string) => {
    if (iMustDiscard) {
      setToDiscard((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : cur.length < 3 ? [...cur, c] : cur));
      return;
    }
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    if (legal.jokerNeedsSuit && c.startsWith('JK')) {
      setJokerPending(c);
      return;
    }
    play(c);
  };

  const confirmDiscard = () => {
    if (toDiscard.length !== 3 || sent) return;
    setSent(true);
    void act({ type: 'discard', cards: toDiscard }).catch(() => setSent(false));
  };

  const teamPill = (team: number) => (
    <span className={`pill team-pill ${team === myTeam ? 'mine' : ''}`} key={team}>
      {team === myTeam ? t('sp.us') : t('sp.them')}: {view.teamScores[team] ?? 0}
      {playing && ` · ${teamTricks(team)}${declarerTeam === team && contract ? `/${contract.tricks}` : ''}`}
    </span>
  );

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[fhTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === fhPartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {bidTag(s) && <span className="tag">{bidTag(s)}</span>}
                {!bidding && view.declarer === s && contract && <span className="tag seen">{contractLabel(contract)}</span>}
                {playing && <span className="tag">{view.tricksWon[s] ?? 0}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('sp.round', { n: view.round })}
            {!bidding && contract && (
              <>
                {' · '}
                {t('fh.contract')}{' '}
                <span style={{ color: isRed(contract.suit) ? '#ff8a8a' : undefined, fontSize: '1.15em' }}>{contractLabel(contract)}</span>
              </>
            )}
            {view.phase === 'KITTY' && !myTurn && turn?.seats[0] !== undefined && ` · ${t('fh.choosingDiscard', { name: name(turn.seats[0]) })}`}
            {bidding && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
          </div>
          {!(bidding && myTurn) && (
            <div className="row" style={{ justifyContent: 'center', gap: 6 }} aria-label={t('table.scores')}>
              {teamPill(myTeam)}
              {teamPill(1 - myTeam)}
            </div>
          )}
          {playing && view.trump === 'N' && view.ledSuit && view.trick.length > 0 && <div className="center-note">{t('fh.follow', { suit: SUIT_SYMBOL[view.ledSuit]! })}</div>}
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'ROUND_END' && view.roundResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>
                {contractLabel(view.roundResults.at(-1)!.contract)} · {view.roundResults.at(-1)!.made ? t('fh.made') : t('fh.failed')}
              </strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.roundResults.at(-1)!.teams.map((r, team) => (
                  <span key={team} className={`pill ${r.delta >= 0 ? 'accent' : 'danger'}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: {r.delta >= 0 ? '+' : ''}{r.delta}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.kind === 'bid' && <BidPicker minValue={legal.minValue} canPass={legal.canPass} hand={view.hand} onBid={(c) => void act({ type: 'bid', tricks: c.tricks, suit: c.suit })} onPass={() => void act({ type: 'pass' })} />}
        {iMustDiscard && (
          <div className="bid-picker" role="group" aria-label={t('fh.discardTitle')}>
            <div className="bid-head">
              <strong>{t('fh.discardTitle')}</strong>
              <span className="dim">{t('hr.pickThree', { n: toDiscard.length })}</span>
            </div>
            <button className="btn primary block bid-confirm" disabled={toDiscard.length !== 3 || sent} onClick={confirmDiscard}>
              {t('fh.discardBtn')}
            </button>
          </div>
        )}
        {jokerPending && (
          <div className="bid-picker" role="group" aria-label={t('fh.nameSuit')}>
            <div className="bid-head">
              <strong>{t('fh.nameSuit')}</strong>
            </div>
            <div className="bid-chips" role="group">
              {(['S', 'H', 'D', 'C'] as const).map((s) => (
                <button
                  key={s}
                  className="bid-chip"
                  style={{ color: isRed(s) ? 'var(--danger)' : undefined, fontSize: '1.6em' }}
                  aria-label={s === 'S' ? 'Spades' : s === 'H' ? 'Hearts' : s === 'D' ? 'Diamonds' : 'Clubs'}
                  onClick={() => {
                    const card = jokerPending;
                    setJokerPending(null);
                    play(card, s);
                  }}
                >
                  {SUIT_SYMBOL[s]}
                </button>
              ))}
            </div>
          </div>
        )}
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={bidding ? i : undefined}
              selected={selected === c || toDiscard.includes(c) || jokerPending === c}
              playable={myTurn && (iMustDiscard || playable.has(c))}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((row, r) => (
              <tr key={r}>
                <td>R{r + 1}</td>
                {row.teams.map((x, team) => (
                  <td key={team} className="num" style={{ color: x.delta < 0 ? 'var(--danger)' : undefined }}>
                    {x.delta >= 0 ? '+' : ''}{x.delta}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {x.declarer ? `★ ${contractLabel(row.contract)} · ${x.tricks}` : x.tricks}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('fh.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}

/**
 * Bid picker: a grid of tricks (rows) by suit (columns). Contracts that no longer beat the current bid are disabled, the
 * hand's best-supported contract is starred, and a separate confirm button means a stray tap never commits a bid.
 */
function BidPicker({ minValue, canPass, hand, onBid, onPass }: { minValue: number; canPass: boolean; hand: string[]; onBid: (c: FHContract) => void; onPass: () => void }) {
  const t = useT();
  const open = useMemo(() => fhAllContracts().filter((c) => fhBidValue(c) > minValue), [minValue]);
  const starred = useMemo(() => {
    const options = BID_SUITS.map((suit) => ({ suit, tricks: Math.min(10, Math.floor(estimateFiveHundredTricks(hand as never, suit) + 1)) }))
      .filter((c) => c.tricks >= 6 && fhBidValue(c) > minValue)
      .sort((a, b) => fhBidValue(a) - fhBidValue(b));
    return options[0] ?? null;
  }, [hand, minValue]);
  const [value, setValue] = useState<FHContract | null>(starred ?? open[0] ?? null);
  const [sent, setSent] = useState(false);
  const send = (fn: () => void) => {
    setSent(true);
    fn();
    setTimeout(() => setSent(false), 1500);
  };
  const isOpen = (c: FHContract) => open.some((o) => o.tricks === c.tricks && o.suit === c.suit);
  return (
    <div className="bid-picker" role="group" aria-label={t('fh.bid')}>
      <div className="bid-head">
        <strong>{t('fh.yourBid')}</strong>
        {starred && <span className="dim">★ {t('cb.suggested')}: <b>{contractLabel(starred)}</b></span>}
      </div>
      <table className="fh-bid-grid">
        <tbody>
          {[6, 7, 8, 9, 10].map((tricks) => (
            <tr key={tricks}>
              {BID_SUITS.map((suit) => {
                const c = { tricks, suit };
                const on = value?.tricks === tricks && value.suit === suit;
                return (
                  <td key={suit} style={{ padding: 0 }}>
                    <button
                      className={`bid-chip ${on ? 'on' : ''} ${starred?.tricks === tricks && starred.suit === suit ? 'star' : ''}`}
                      style={{ color: !on && isRed(suit) ? 'var(--danger)' : undefined }}
                      disabled={!isOpen(c)}
                      aria-pressed={on}
                      aria-label={contractLabel(c)}
                      onClick={() => setValue(c)}
                    >
                      {contractLabel(c)}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row" style={{ gap: 8 }}>
        {canPass && (
          <button className="btn grow" disabled={sent} onClick={() => send(onPass)}>
            {t('ta.pass')}
          </button>
        )}
        {value && (
          <button className="btn primary grow bid-confirm" disabled={sent || !isOpen(value)} onClick={() => send(() => onBid(value))}>
            {t('sp.bidValue', { n: contractLabel(value) })}
          </button>
        )}
      </div>
    </div>
  );
}
